import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "col span-6" }
const _hoisted_3 = { class: "col span-6" }
const _hoisted_4 = { class: "col span-6" }

import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import { LabeledInput } from '@components/Form/LabeledInput';
import { RadioGroup } from '@components/Form/Radio';
import { RcSection, SECTION_TYPE } from '@components/RcSection';
import UnitInput from '@shell/components/form/UnitInput.vue';
import RcS3Config from '@shell/edit/provisioning.cattle.io.cluster/tabs/etcd/RcS3Config.vue';
import { RETENTION_DEFAULT } from '@shell/edit/provisioning.cattle.io.cluster/shared';
import isEmpty from 'lodash/isEmpty';

export interface EtcdSnapshotS3 {
  bucket?: string;
  cloudCredentialName?: string | null;
  endpoint?: string;
  endpointCA?: string;
  folder?: string;
  region?: string;
  retention?: number | null;
  skipSSLVerify?: boolean;
}

export interface EtcdSnapshotConfigValue {
  disableSnapshots?: boolean;
  snapshotScheduleCron?: string;
  snapshotRetention?: number | null;
  s3?: EtcdSnapshotS3 | null;
  [key: string]: unknown;
}

const SNAPSHOT_SCHEDULE_CRON_DISTRO_DEFAULT = '0 */12 * * *';

export default /*@__PURE__*/_defineComponent({
  ...{ name: 'RcEtcdSnapshotConfig' },
  __name: 'RcEtcdSnapshotConfig',
  props: {
    mode: {},
    value: { default: null },
    s3Backup: { type: Boolean, default: false },
    namespace: {},
    registerBeforeHook: {}
  },
  emits: ["update:value", "s3-backup-changed", "validation-changed"],
  setup(__props: any, { emit: __emit }) {




const props = __props;

const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

const etcd = computed<EtcdSnapshotConfigValue>(() => props.value || {});
// An unset schedule/retention (empty, or 0 as returned for an imported cluster) isn't written to the cluster, which then
// keeps its own settings. So show them empty, with the RKE2/K3s defaults as placeholders
const snapshotRetention = computed(() => etcd.value.snapshotRetention || undefined);
const snapshotsEnabled = computed(() => etcd.value.disableSnapshots !== true);

const disableSnapshotsOptions = computed(() => [
  { value: true, label: t('generic.disable') },
  { value: false, label: t('generic.enable') },
]);
const s3BackupOptions = computed(() => [
  { value: false, label: t('generic.disable') },
  { value: true, label: t('generic.enable') },
]);

function update(changes: Partial<EtcdSnapshotConfigValue>) {
  emit('update:value', { ...etcd.value, ...changes });
}

function s3BackupChanged(enabled: boolean) {
  // Keep any existing s3 config when (re)enabling, otherwise editing a cluster with s3 defined would clear it
  update({ s3: enabled ? (isEmpty(etcd.value.s3) ? {} : etcd.value.s3) : null });
  emit('s3-backup-changed', enabled);
  emit('validation-changed', !enabled);
}

function disableSnapshotsChanged(disableSnapshots: boolean) {
  if (disableSnapshots) {
    update({ disableSnapshots, s3: null });
    emit('s3-backup-changed', false);
    emit('validation-changed', true);
  } else {
    update({ disableSnapshots });
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(RcSection), {
    title: _unref(t)('cluster.rke2.etcd.disableSnapshots.label'),
    mode: "with-header",
    type: _unref(SECTION_TYPE).SECONDARY,
    expandable: "",
    "data-testid": "etcd-automatic-backups"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(RadioGroup), {
            value: !snapshotsEnabled.value,
            name: "etcd-disable-snapshots",
            "data-testid": "etcd-disable-snapshots",
            options: disableSnapshotsOptions.value,
            mode: __props.mode,
            "onUpdate:value": disableSnapshotsChanged
          }, null, 8, ["value", "options", "mode"])
        ])
      ]),
      _cache[6] || (_cache[6] = _createTextVNode()),
      (snapshotsEnabled.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["row", { 'has-cron-hint': !!etcd.value.snapshotScheduleCron }])
          }, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_unref(LabeledInput), {
                value: etcd.value.snapshotScheduleCron,
                type: "cron",
                placeholder: SNAPSHOT_SCHEDULE_CRON_DISTRO_DEFAULT,
                "data-testid": "etcd-snapshot-schedule-cron",
                mode: __props.mode,
                label: _unref(t)('cluster.rke2.etcd.snapshotScheduleCron.label'),
                "onUpdate:value": _cache[0] || (_cache[0] = (snapshotScheduleCron) => update({ snapshotScheduleCron }))
              }, null, 8, ["value", "mode", "label"])
            ]),
            _cache[4] || (_cache[4] = _createTextVNode()),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(UnitInput, {
                value: snapshotRetention.value,
                placeholder: _unref(RETENTION_DEFAULT),
                "data-testid": "etcd-snapshot-retention",
                mode: __props.mode,
                label: _unref(t)('cluster.rke2.etcd.snapshotRetention.label'),
                suffix: __props.s3Backup ? _unref(t)('cluster.rke2.snapshots.s3Suffix') : _unref(t)('cluster.rke2.snapshots.suffix'),
                tooltip: __props.s3Backup ? _unref(t)('cluster.rke2.etcd.snapshotRetention.tooltip') : undefined,
                "onUpdate:value": _cache[1] || (_cache[1] = (snapshotRetention) => update({ snapshotRetention }))
              }, null, 8, ["value", "placeholder", "mode", "label", "suffix", "tooltip"])
            ])
          ], 2))
        : _createCommentVNode("", true),
      _cache[7] || (_cache[7] = _createTextVNode()),
      (snapshotsEnabled.value)
        ? (_openBlock(), _createBlock(_unref(RcSection), {
            key: 1,
            title: _unref(t)('cluster.rke2.etcd.s3backups.label'),
            mode: "with-header",
            type: _unref(SECTION_TYPE).SECONDARY,
            expandable: "",
            expanded: false,
            "data-testid": "etcd-s3-backups"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(RadioGroup), {
                value: __props.s3Backup,
                name: "etcd-s3",
                "data-testid": "etcd-s3-backup",
                options: s3BackupOptions.value,
                mode: __props.mode,
                "onUpdate:value": s3BackupChanged
              }, null, 8, ["value", "options", "mode"]),
              _cache[5] || (_cache[5] = _createTextVNode()),
              (__props.s3Backup)
                ? (_openBlock(), _createBlock(RcS3Config, {
                    key: 0,
                    value: etcd.value.s3 || undefined,
                    "local-retention-count": snapshotRetention.value,
                    namespace: __props.namespace,
                    "register-before-hook": __props.registerBeforeHook,
                    mode: __props.mode,
                    "onUpdate:value": _cache[2] || (_cache[2] = (s3) => update({ s3 })),
                    onValidationChanged: _cache[3] || (_cache[3] = (valid) => emit('validation-changed', valid))
                  }, null, 8, ["value", "local-retention-count", "namespace", "register-before-hook", "mode"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["title", "type"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["title", "type"]))
}
}

})